"use client";

import Link from "next/link";
import { ArrowRight, Eye, EyeOff, LoaderCircle, Lock, UserRound } from "lucide-react";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { StatusPanel } from "@/components/auth-ui";
import { SignInCodeForm } from "@/components/sign-in-code-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toUserErrorMessage } from "@/lib/error-config";
import { useFacilityService } from "@/lib/facility-service";
import { useFrappeSession } from "@/lib/frappe-session";
import { clearMfaContext, saveMfaContext } from "@/lib/mfa-flow";
import { resolvePostLoginPath } from "@/lib/navigation";

/* The one sign-in form: username/email + password, then either the one-time
   code step or straight to the destination. The sign-in page opens the code
   step at /sign-in/verify; the stepper's "Email verified" card passes
   onCodeStep and shows it in place, so onboarding stays on the stepper. */

const schema = z.object({
  username: z.string().min(1, "Enter your username or email"),
  password: z.string().min(8, "Enter your password"),
});
type Values = z.infer<typeof schema>;

export function SignInForm({ nextPath, defaultUsername = "", autoFocusPassword = false, footer, onCodeStep }: {
  /** Where to go once signed in (already validated with safeNextPath by the caller). */
  nextPath: string;
  defaultUsername?: string;
  /** Focus the password when the username is already known (prefilled). */
  autoFocusPassword?: boolean;
  footer?: React.ReactNode;
  /** Show the code step in place of the password form; called as it opens and closes. */
  onCodeStep?: (active: boolean) => void;
}) {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [failedMessage, setFailedMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [codeStep, setCodeStep] = useState(false);
  const { login } = useFrappeSession();
  const facilityService = useFacilityService();
  const { register, handleSubmit, formState: { errors } } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { username: defaultUsername, password: "" },
  });

  const onSubmit = async (values: Values) => {
    setFailedMessage(""); setIsSubmitting(true);
    try {
      const response = (await login({ username: values.username, password: values.password })) as unknown as {
        tmp_id?: string;
        verification?: unknown;
      };
      if (response?.tmp_id || response?.verification) {
        // Frappe 2FA: step 1 returns a short-lived tmp_id. It is NOT in a
        // cookie — the client must echo it back with the OTP in step 2.
        // Keep it in sessionStorage (never localStorage) alongside the destination.
        saveMfaContext({ nextPath, method: "email", tmpId: response?.tmp_id });
        if (onCodeStep) { setCodeStep(true); onCodeStep(true); setIsSubmitting(false); }
        else router.push("/sign-in/verify");
      } else {
        clearMfaContext();
        // Onboarding gate: facilities still on the stepper resume setup
        // instead of landing on the dashboard.
        window.location.assign(await resolvePostLoginPath(() => facilityService.onboardingStatus(), nextPath));
      }
    } catch (error) {
      setFailedMessage(toUserErrorMessage(error, "SIGN_IN_FAILED"));
      // Only re-enable on failure: on success the button stays busy until
      // the navigation replaces the page, so it can't be submitted twice.
      setIsSubmitting(false);
    }
  };

  if (codeStep) {
    return <SignInCodeForm onRestart={() => { clearMfaContext(); setCodeStep(false); onCodeStep?.(false); }} />;
  }

  return <form className="auth-stack" onSubmit={handleSubmit(onSubmit)} noValidate>
    <div className="auth-field">
      <label className="auth-label" htmlFor="username">Username or email</label>
      <div className="auth-input-icon"><UserRound size={18} /><Input id="username" autoComplete="username" placeholder="Enter your username or email" aria-invalid={Boolean(errors.username)} aria-describedby={errors.username ? "username-error" : undefined} {...register("username")} /></div>
      {errors.username && <span id="username-error" className="field-error">{errors.username.message}</span>}
    </div>
    <div className="auth-field">
      <div className="auth-label-row"><label className="auth-label" htmlFor="password">Password</label><Link href="/recover-access">Forgot password?</Link></div>
      <div className="auth-input-icon password-control"><Lock size={18} /><Input id="password" type={showPassword ? "text" : "password"} autoComplete="current-password" placeholder="Enter your password" autoFocus={autoFocusPassword} aria-invalid={Boolean(errors.password)} aria-describedby={errors.password ? "password-error" : undefined} {...register("password")} /><Button variant="quiet" size="sm" type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Hide password" : "Show password"}>{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}</Button></div>
      {errors.password && <span id="password-error" className="field-error">{errors.password.message}</span>}
    </div>
    {failedMessage && <StatusPanel tone="danger" title="Sign-in unsuccessful">{failedMessage}</StatusPanel>}
    <Button type="submit" disabled={isSubmitting}>{isSubmitting ? <><LoaderCircle size={16} className="spin" />Checking your details…</> : <>Continue <ArrowRight size={16} /></>}</Button>
    <p className="auth-lock-note"><Lock size={14} />Next, we’ll ask for a one-time verification code.</p>
    {footer}
  </form>;
}
